/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { get, set, action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import {
  filterValue,
  getNumericYearLevelFilter,
} from '@linc-technologies/linced-common/utils/filtering';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';

export default class AdminController extends Controller {
  Jurisdiction = Jurisdiction;

  @service
  session;

  @service
  fieldOptions;

  @service
  options;

  @service
  permissions;

  @service
  toaster;

  @tracked
  sickbayVisitForDetails = null;
  @tracked
  detailsDialogOpen = false;
  @tracked
  filters = [];

  @computed('session.activeSchool', 'fieldOptions.yearLevelOptions.[]')
  get yearLevelOptions() {
    let options = findBy(
      this.get('fieldOptions.yearLevelOptions'),
      'schoolId',
      this.get('session.activeSchool'),
    );
    if (isEmpty(options) || isEmpty(options.options)) {
      return [];
    }
    return options.options;
  }

  @computed('model.sickbayVisits.[]', 'filters.@each.{field,value}')
  get filteredSickbayVisits() {
    if (isEmpty(this.model.sickbayVisits)) {
      return [];
    }
    if (isEmpty(this.filters)) {
      // No filters - just return the data.
      return this.model.sickbayVisits;
    }

    // Determine datatypes for any specified filters.
    let filters = this.filters.map((filter) => {
      return {
        key: filter.field,
        dataType: DataType.STRING,
        value: filter.value,
      };
    });

    // Determine numeric filter for year level, if applicable.
    let yearLevelFilterValue = getNumericYearLevelFilter(
      findBy(filters, 'key', 'yearLevel'),
      this.yearLevelOptions,
    );

    // Apply all filters to each row in one hit, to avoid iterating over a
    // (potentially) large dataset multiple times.

    return this.model.sickbayVisits.filter((row) => {
      return filters.every((filter) => {
        // Field-specific filter processing.
        switch (filter.key) {
          case 'yearLevel':
            return filterValue(
              row.get('learner.numericCurrentYearLevel') ?? '',
              DataType.INTEGER,
              yearLevelFilterValue,
            );
        }

        // Generic filter processing.
        return filterValue(
          get(row, filter.key) ?? '',
          filter.dataType,
          filter.value,
        );
      });
    });
  }

  @computed('session.scopes.[]')
  get isTenantAdmin() {
    return this.permissions.meet(Scopes.TENANT_ADMIN);
  }

  @optionAlias('core:staffPermissions:medicationsForTeachers')
  medicationsForTeachers;

  @computed('isTenantAdmin', 'medicationsForTeachers')
  get canAdministerMedications() {
    return this.isTenantAdmin || this.medicationsForTeachers;
  }

  @action
  openDetailsDialog(sickbayVisit) {
    this.sickbayVisitForDetails = sickbayVisit;
    this.detailsDialogOpen = true;
  }

  @action
  closeDetailsDialog() {
    this.sickbayVisitForDetails = null;
    this.detailsDialogOpen = false;
  }

  @action
  filter(field, value) {
    let filter = findBy(this.filters, 'field', field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      this.filters = [...this.filters, { field, value }];
      return;
    }

    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      this.filters = this.filters.filter((f) => f !== filter);
    } else {
      // Existing filter has been changed; update it.
      set(filter, 'value', value);
    }
  }
}
